import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    TextField,
    InputAdornment,
    Button,
    Alert,
    Skeleton,
    TableSortLabel,
} from '@mui/material';
import { Search, History as HistoryIcon } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { WarehouseAPI } from '../services/api';
import { PackagingHistoryEntry } from '../types';
import { useUrlParam, useSetUrlParams } from '../components/common/useUrlParam';
import TablePaginationBar, { DEFAULT_PAGE_SIZE } from '../components/common/TablePaginationBar';

const PAGE_SIZE = DEFAULT_PAGE_SIZE;

// Sortable fields exposed by GET /history (whitelisted server-side). The sort applies to the
// FULL matching dataset before pagination, so it's a global sort, not a per-page one.
const SORT_COLUMNS: { key: string; label: string }[] = [
    { key: 'updatedAt', label: 'Last updated' },
    { key: 'sku', label: 'SKU code' },
    { key: 'packagingCount', label: 'Packaging count' },
];

// Default sort direction when a column first becomes active: SKU reads naturally A→Z, the
// recency/count columns most-first.
const DEFAULT_DIR_FOR_KEY: Record<string, 'asc' | 'desc'> = {
    updatedAt: 'desc',
    sku: 'asc',
    packagingCount: 'desc',
};

// Human-readable IST timestamp (medium date + short time). The ' IST' suffix is appended in
// the free-text caption below, matching the house convention (suffix-free formatter output).
const fmtIst = (iso: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(d);
};

const CardingPackagingHistoryTab: React.FC = () => {
    // Search and page both live in the URL ("hq"/"hpage" prefixes to avoid colliding with the
    // other Carding sub-tabs) so a refresh — or a shared link — restores exactly where the user
    // was, mirroring the Carding tab's URL-param state.
    const [searchQuery, setSearchQuery] = useUrlParam('hq');
    const [pageParam, setPageParam] = useUrlParam('hpage', '0');
    const page = Math.max(0, parseInt(pageParam, 10) || 0);
    const setPage = (v: number) => setPageParam(v === 0 ? '' : String(v));

    // Sort state also lives in the URL ("hsort"/"hdir"), mirroring the page/search params.
    // Default is most-recent-first (updatedAt desc), so both keys are absent from a clean URL.
    const [sortBy] = useUrlParam('hsort', 'updatedAt');
    const [sortDirParam] = useUrlParam('hdir', 'desc');
    const sortDir: 'asc' | 'desc' = sortDirParam === 'asc' ? 'asc' : 'desc';
    const setUrlParams = useSetUrlParams();

    // Clicking a sort column toggles direction when it's already active, otherwise switches to
    // that column at its natural default direction. hsort/hdir/hpage are written together (the
    // sort change resets to page 0); empty values drop the key to keep the clean-URL convention.
    const handleSort = (key: string) => {
        const nextDir: 'asc' | 'desc' =
            sortBy === key ? (sortDir === 'asc' ? 'desc' : 'asc') : (DEFAULT_DIR_FOR_KEY[key] ?? 'desc');
        setUrlParams({
            hsort: key === 'updatedAt' ? '' : key,
            hdir: nextDir === 'desc' ? '' : nextDir,
            hpage: '',
        });
    };

    // Debounce the search term that drives the query/URL so typing doesn't fire a request (or
    // rewrite the URL) on every keystroke. The input itself stays controlled on `draft`.
    const [draft, setDraft] = useState(searchQuery);
    useEffect(() => { setDraft(searchQuery); }, [searchQuery]);
    useEffect(() => {
        if (draft === searchQuery) return;
        const t = setTimeout(() => {
            // A new search term resets to page 1 (clear "hpage") so results aren't shown on a
            // stale page index.
            setSearchQuery(draft, ['hpage']);
        }, 300);
        return () => clearTimeout(t);
    }, [draft, searchQuery, setSearchQuery]);

    const reduceMotion = useReducedMotion();

    // page/pageSize/search are all in the query key, so each page+search combination is cached
    // and switching back is instant. keepPreviousData keeps the current page rendered while the
    // next one loads, so paging doesn't flash the skeleton.
    const historyQuery = useQuery({
        queryKey: ['packaging-mapping-history', page, PAGE_SIZE, searchQuery, sortBy, sortDir],
        queryFn: () => WarehouseAPI.getPackagingMappingHistory({ page, pageSize: PAGE_SIZE, search: searchQuery, sortBy, sortDir }),
        placeholderData: keepPreviousData,
        staleTime: 30_000,
    });

    const skus = historyQuery.data?.data ?? [];
    const total = historyQuery.data?.total ?? 0;
    const loading = historyQuery.isLoading;
    const error = historyQuery.error
        ? ((historyQuery.error as any)?.response?.data?.message
            || (historyQuery.error as any)?.message
            || 'Failed to load packaging history')
        : null;

    const searchBox = (
        <TextField
            size="small"
            placeholder="Search by SKU or packaging SKU…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            sx={{ minWidth: 280, bgcolor: '#ffffff' }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Search sx={{ fontSize: 18, color: '#000000' }} />
                    </InputAdornment>
                ),
            }}
        />
    );

    const sortToolbar = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Sort by:
            </Typography>
            {SORT_COLUMNS.map(col => (
                <TableSortLabel
                    key={col.key}
                    active={sortBy === col.key}
                    direction={sortBy === col.key ? sortDir : 'asc'}
                    onClick={() => handleSort(col.key)}
                    sx={{ fontSize: '0.8rem' }}
                >
                    {col.label}
                </TableSortLabel>
            ))}
        </Box>
    );

    if (loading) {
        return (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    {searchBox}
                    {sortToolbar}
                </Box>
                {[1, 2, 3].map(i => (
                    <Skeleton key={i} variant="rounded" height={120} sx={{ mb: 2, borderRadius: 2 }} />
                ))}
            </Box>
        );
    }

    if (error) {
        return (
            <Alert
                severity="error"
                action={<Button onClick={() => historyQuery.refetch()}>Retry</Button>}
                sx={{ m: 2 }}
            >
                {error}
            </Alert>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {searchBox}
                <Chip
                    icon={<HistoryIcon sx={{ fontSize: 16 }} />}
                    label={`${total} SKUs with history`}
                    size="small"
                    sx={{ fontWeight: 600, bgcolor: '#FAFAF7' }}
                />
                {sortToolbar}
            </Box>

            {skus.length === 0 ? (
                <Alert severity="info">No packaging mapping history yet — edit or import a mapping in the Carding tab to start the changelog.</Alert>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {skus.map((skuEntry, idx) => (
                        <Box
                            key={skuEntry.sku}
                            component={motion.div}
                            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.22, delay: Math.min(idx, 12) * 0.03, ease: 'easeOut' }}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                p: 2,
                                bgcolor: '#ffffff',
                            }}
                        >
                            {/* Header: mono SKU + current packaging set */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.95rem' }}>
                                    {skuEntry.sku}
                                </Typography>
                                {skuEntry.currentPackagingSkus.length > 0 ? (
                                    <Chip
                                        label={`Now: ${skuEntry.currentPackagingSkus.join(', ')}`}
                                        size="small"
                                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem', bgcolor: '#F1F2DA', color: '#4A433D' }}
                                    />
                                ) : (
                                    <Chip
                                        label="No packaging linked"
                                        size="small"
                                        variant="outlined"
                                        sx={{ fontSize: '0.7rem', borderStyle: 'dashed' }}
                                    />
                                )}
                            </Box>

                            {/* Timeline — newest at top (entries arrive oldest->newest, reverse for display). */}
                            <Box sx={{ pl: 1 }}>
                                {skuEntry.entries.map((entry, i, arr) => {
                                    // arr is oldest->newest; display index: top node is the last entry.
                                    const displayEntry: PackagingHistoryEntry = arr[arr.length - 1 - i];
                                    const isOldest = i === arr.length - 1;
                                    const isBaseline = isOldest && (displayEntry.note || '').toLowerCase().startsWith('baseline');
                                    const isLast = i === arr.length - 1;
                                    return (
                                        <Box key={`${displayEntry.at}-${i}`} sx={{ display: 'flex', gap: 1.5 }}>
                                            {/* Rail: dot + connecting line */}
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 16 }}>
                                                <Box sx={{
                                                    width: 10,
                                                    height: 10,
                                                    borderRadius: '50%',
                                                    bgcolor: i === 0 ? '#DC5829' : '#B8A99B',
                                                    mt: 0.5,
                                                    flexShrink: 0,
                                                }} />
                                                {!isLast && (
                                                    <Box sx={{ width: 2, flexGrow: 1, bgcolor: 'divider', my: 0.5, minHeight: 24 }} />
                                                )}
                                            </Box>

                                            {/* Node content */}
                                            <Box sx={{ pb: isLast ? 0 : 2, flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
                                                        {fmtIst(displayEntry.at)} IST
                                                    </Typography>
                                                    {isBaseline && (
                                                        <Chip label="Baseline" size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#FAFAF7', color: '#6B625B' }} />
                                                    )}
                                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                        by {displayEntry.by || 'system'}
                                                        {displayEntry.note ? ` · ${displayEntry.note}` : ''}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                                                    {displayEntry.added.map(p => (
                                                        <Chip key={`a-${p}`} label={`+ ${p}`} size="small"
                                                            sx={{ fontFamily: 'monospace', fontSize: '0.68rem', bgcolor: '#dcfce7', color: '#15803d' }} />
                                                    ))}
                                                    {displayEntry.removed.map(p => (
                                                        <Chip key={`r-${p}`} label={`− ${p}`} size="small"
                                                            sx={{ fontFamily: 'monospace', fontSize: '0.68rem', bgcolor: '#fee2e2', color: '#b91c1c' }} />
                                                    ))}
                                                    {displayEntry.added.length === 0 && displayEntry.removed.length === 0 && (
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                            No change recorded
                                                        </Typography>
                                                    )}
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                                        Now linked:
                                                    </Typography>
                                                    {displayEntry.resultingSet.length > 0 ? (
                                                        displayEntry.resultingSet.map(p => (
                                                            <Chip key={`s-${p}`} label={p} size="small"
                                                                sx={{ fontFamily: 'monospace', fontSize: '0.68rem', bgcolor: '#F1F2DA', color: '#4A433D' }} />
                                                        ))
                                                    ) : (
                                                        <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                                            (none)
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    ))}
                </Box>
            )}

            <TablePaginationBar page={page} total={total} rowsPerPage={PAGE_SIZE} onPageChange={setPage} />
        </Box>
    );
};

export default CardingPackagingHistoryTab;
